import { z } from 'zod';

/**
 * Indian State Codes for GSTIN
 */
export const STATE_CODES: Record<string, string> = {
  'AN': '35', 'AP': '37', 'AR': '12', 'AS': '18', 'BR': '10', 'CH': '04', 'CT': '22',
  'DN': '26', 'DD': '25', 'DL': '07', 'GA': '30', 'GJ': '24', 'HR': '06', 'HP': '02',
  'JK': '01', 'JH': '20', 'KA': '29', 'KL': '32', 'LD': '31', 'MP': '23', 'MH': '27',
  'MN': '14', 'ML': '17', 'MZ': '15', 'NL': '13', 'OR': '21', 'PY': '34', 'PB': '03',
  'RJ': '08', 'SK': '11', 'TN': '33', 'TS': '36', 'TR': '16', 'UP': '09', 'UK': '05',
  'WB': '19',
};

export const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
export const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
export const CIN_REGEX = /^[LU]{1}[0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/;

export const panSchema = z.string().regex(PAN_REGEX, 'Invalid PAN format (e.g. ABCDE1234F)');

export const gstinSchema = (stateCodeField: string) => 
  z.string()
    .regex(GSTIN_REGEX, 'Invalid GSTIN format')
    .superRefine((gstin, ctx) => {
      // We can't easily cross-reference another field in a simple string schema 
      // without being inside an object schema's refine.
      // This is a placeholder for the logic; it will be used in the object refinement.
    });

export const companyBaseSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  legalName: z.string().min(1, 'Legal Name is required'),
  cin: z.string().regex(CIN_REGEX, 'Invalid CIN format').optional().nullable().or(z.literal('')),
  gstin: z.string().regex(GSTIN_REGEX, 'Invalid GSTIN format').optional().nullable().or(z.literal('')),
  pan: panSchema,
  registeredAddress: z.object({
    line1: z.string().min(1, 'Address line 1 is required'),
    line2: z.string().optional(),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    pincode: z.string().regex(/^[0-9]{6}$/, 'Invalid pincode'),
  }),
  stateCode: z.string().min(2, 'State code is required'),
  rocNumber: z.string().optional().nullable(),
  foremanName: z.string().min(3, 'Foreman name must be at least 3 characters'),
});

export const companySchema = companyBaseSchema.refine((data) => {
  if (data.gstin && data.stateCode) {
    const expectedPrefix = STATE_CODES[data.stateCode];
    if (expectedPrefix && !data.gstin.startsWith(expectedPrefix)) {
      return false;
    }
  }
  return true;
}, {
  message: 'GSTIN state code prefix does not match the selected state',
  path: ['gstin'],
});
